import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { AuthContext } from "../../Context/AuthContext";
import { logActivity } from "../../utils/LogActivity";
import { Authorization } from "../../Context/Authorization";
import Select from "react-select";

const inputStyle = { width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff", outline: "none", transition: "border-color 0.2s" };
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };

const AddRangeForm = ({ onClose, onAddRange }) => {
  const [rangeName, setRangeName] = useState("");
  const [divisionId, setDivisionId] = useState("");
  const [division, setDivision] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [errors, setErrors] = useState({});
  const { userId } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const config = Authorization();
        const [divisionRes, districtRes] = await Promise.all([
          axios.get(`${Api}/api/divisions`, config),
          axios.get(`${Api}/api/lgd/districts`, config),
        ]);
        if (Array.isArray(divisionRes.data)) setDivision(divisionRes.data);
        if (Array.isArray(districtRes.data)) {
          setDistricts(districtRes.data.map((d) => ({ value: d.districtCode, label: d.districtName })));
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchInitialData();
  }, [Api]);

  const handleDistrictChange = async (selected) => {
    setSelectedDistrict(selected);
    setBlocks([]);
    setSelectedBlocks([]);
    try {
      const config = Authorization();
      const res = await axios.get(`${Api}/api/lgd/blocks?districtCode=${selected.value}`, config);
      if (Array.isArray(res.data.blocks)) {
        setBlocks(res.data.blocks.map((b) => ({ value: b.blockCode, label: b.blockName })));
      } else {
        toast.error("Blocks not found for the selected district");
      }
    } catch (error) {
      console.error("Error fetching blocks:", error);
      toast.error("Failed to load blocks");
    }
  };

  const validate = () => {
    const errs = {};
    if (!rangeName) errs.rangeName = "Range name is required";
    if (!divisionId) errs.divisionId = "Please select Division";
    if (!selectedDistrict) errs.district = "Please select District";
    if (selectedBlocks.length === 0) errs.blocks = "Select at least one Block";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); toast.error("Please fill all required fields"); return; }

    const formData = {
      rangeName,
      divisionId,
      districtCode: selectedDistrict.value,
      districtName: selectedDistrict.label,
      blocks: selectedBlocks.map((b) => ({ blockCode: b.value, blockName: b.label })),
    };

    try {
      const config = Authorization();
      const response = await axios.post(`${Api}/api/ranges`, formData, config);
      if (response.status === 200 || response.status === 201) {
        toast.success("Range added successfully");
        await logActivity(userId, "New Range Added", "Add Range Page", Api);
        onAddRange(response.data);
        onClose();
      } else {
        toast.error("Failed to submit form");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error submitting form");
    }
  };

  const focus = (e) => (e.target.style.borderColor = "#4CAF50");
  const blur = (e) => (e.target.style.borderColor = "#E2E8F0");

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", position: "sticky", top: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Add Range</span>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 6, color: "#fff", cursor: "pointer", fontSize: 16, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>
        <div style={{ padding: 24, overflowY: "auto", maxHeight: "calc(90vh - 60px)" }}>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Range Name <span style={{ color: "#E53E3E" }}>*</span></label>
              <input type="text" placeholder="Enter range name" value={rangeName}
                onChange={(e) => setRangeName(e.target.value)} style={inputStyle} onFocus={focus} onBlur={blur} />
              {errors.rangeName && <div style={{ color: "#E53E3E", fontSize: 11, marginTop: 4 }}>{errors.rangeName}</div>}
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Division <span style={{ color: "#E53E3E" }}>*</span></label>
              <select value={divisionId} onChange={(e) => setDivisionId(e.target.value)}
                style={{ ...inputStyle, cursor: "pointer" }} onFocus={focus} onBlur={blur}>
                <option value="">Select Division</option>
                {division.sort((a, b) => a.divisionName.localeCompare(b.divisionName)).map((d) => (
                  <option key={d._id} value={d._id}>{d.divisionName}</option>
                ))}
              </select>
              {errors.divisionId && <div style={{ color: "#E53E3E", fontSize: 11, marginTop: 4 }}>{errors.divisionId}</div>}
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>District <span style={{ color: "#E53E3E" }}>*</span></label>
              <Select options={districts} value={selectedDistrict} onChange={handleDistrictChange} placeholder="Select District" />
              {errors.district && <div style={{ color: "#E53E3E", fontSize: 11, marginTop: 4 }}>{errors.district}</div>}
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Blocks <span style={{ color: "#E53E3E" }}>*</span></label>
              <Select options={blocks} isMulti value={selectedBlocks} onChange={setSelectedBlocks} placeholder="Select Blocks" />
              {errors.blocks && <div style={{ color: "#E53E3E", fontSize: 11, marginTop: 4 }}>{errors.blocks}</div>}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button type="submit" style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)" }}>
                <i className="mdi mdi-check" /> Submit
              </button>
              <button type="button" onClick={onClose} style={{ padding: "10px 20px", background: "#F7FAFC", color: "#4A5568", border: "1px solid #E2E8F0", borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddRangeForm;
